import React, { useCallback, useEffect, useRef } from 'react';
import { cn } from '@/utils/cn';

export interface AutoResizeTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  minHeight?: number;
}

const AutoResizeTextarea = React.forwardRef<HTMLTextAreaElement, AutoResizeTextareaProps>(
  ({ value, onInput, onChange, minHeight = 100, className, ...props }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    // Allow parent ref access
    const setRefs = useCallback(
      (el: HTMLTextAreaElement | null) => {
        textareaRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
      },
      [ref]
    );

    const resizeTextarea = useCallback(
      (el?: HTMLTextAreaElement | null) => {
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.max(minHeight, el.scrollHeight)}px`;
      },
      [minHeight]
    );

    const handleInput = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        resizeTextarea(e.target);
        onInput?.(e);
        onChange?.(e);
      },
      [onInput, onChange, resizeTextarea]
    );

    // Resize when `value` changes (e.g., editing form opened)
    useEffect(() => {
      if (textareaRef.current) {
        resizeTextarea(textareaRef.current);
      }
    }, [value, resizeTextarea]);

    return (
      <textarea
        {...props}
        ref={setRefs}
        value={value}
        onInput={handleInput}
        className={cn(
          'w-full min-h-[100px] rounded-[8px] border border-[#D9D9D9] p-[8px] resize-none overflow-hidden text-[13px] transition-all duration-100 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent focus-visible:border-[#0C8DBF]',
          className
        )}
      />
    );
  }
);

AutoResizeTextarea.displayName = 'AutoResizeTextarea';
export default AutoResizeTextarea;
